import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';

interface InteractionState {
    favorite: boolean;
    wishlist: boolean;
    hasReview: boolean;
}

interface ShopInteractionsContextType {
    interactions: Record<string, InteractionState>;
    fetchInteractionsForShops: (shopIds: string[]) => Promise<void>;
    setBulkInteractions: (data: Record<string, InteractionState>) => void;
    toggleFavorite: (shopId: string) => Promise<boolean>;
    toggleWishlist: (shopId: string) => Promise<boolean>;
    getInteraction: (shopId: string) => InteractionState;
    isLoading: boolean;
}

const ShopInteractionsContext = createContext<ShopInteractionsContextType | undefined>(undefined);

export function ShopInteractionsProvider({ children }: { children: React.ReactNode }) {
    const { isLoggedIn } = useAuth();
    const [interactions, setInteractions] = useState<Record<string, InteractionState>>({});
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useQueryClient();

    // Bulk fetch function
    const fetchInteractionsForShops = useCallback(async (shopIds: string[]) => {
        if (!isLoggedIn || shopIds.length === 0) return;

        // Skip IDs already fetched
        const newShopIds = shopIds.filter(id => !interactions[id]);
        if (newShopIds.length === 0) return;

        setIsLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            const res = await fetch('/api/interactions/bulk/shops', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ shopIds: newShopIds })
            });

            if (res.ok) {
                const data = await res.json();
                setInteractions(prev => ({ ...prev, ...data }));
            }
        } catch (error) {
            console.error("Error fetching bulk interactions:", error);
        } finally {
            setIsLoading(false);
        }
    }, [isLoggedIn]);

    // Manual bulk update (e.g. from dashboard)
    const setBulkInteractions = useCallback((data: Record<string, InteractionState>) => {
        setInteractions(prev => ({ ...prev, ...data }));
    }, []);

    // Toggle Favorite
    const toggleFavorite = useCallback(async (shopId: string) => {
        if (!isLoggedIn) return false;

        // Optimistic update
        const wasFavorite = interactions[shopId]?.favorite || false;
        setInteractions(prev => ({
            ...prev,
            [shopId]: { ...prev[shopId], favorite: !wasFavorite }
        }));

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                // Revert
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], favorite: wasFavorite }
                }));
                return false;
            }

            const res = await fetch(`/api/shops/${shopId}/favorite`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (res.ok) {
                const data = await res.json();
                // Update with server truth
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], favorite: data.favorite }
                }));
                // Sync with dashboard
                queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                return data.favorite;
            } else {
                console.error("Failed to toggle favorite on server:", await res.text());
                // Revert on server error
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], favorite: wasFavorite }
                }));
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
            // Revert on network/other error
            setInteractions(prev => ({
                ...prev,
                [shopId]: { ...prev[shopId], favorite: wasFavorite }
            }));
        }
        return false;
    }, [isLoggedIn, interactions, queryClient]);

    // Toggle Wishlist
    const toggleWishlist = useCallback(async (shopId: string) => {
        if (!isLoggedIn) return false;

        // Optimistic update
        const wasInWishlist = interactions[shopId]?.wishlist || false;
        setInteractions(prev => ({
            ...prev,
            [shopId]: { ...prev[shopId], wishlist: !wasInWishlist }
        }));

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], wishlist: wasInWishlist }
                }));
                return false;
            }

            const res = await fetch(`/api/shops/${shopId}/wishlist`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (res.ok) {
                const data = await res.json();
                // Update with server truth
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], wishlist: data.wishlist }
                }));
                // Sync with dashboard
                queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                return data.wishlist;
            } else {
                console.error("Failed to toggle wishlist on server:", await res.text());
                setInteractions(prev => ({
                    ...prev,
                    [shopId]: { ...prev[shopId], wishlist: wasInWishlist }
                }));
            }
        } catch (error) {
            console.error("Error toggling wishlist:", error);
            // Revert on error
            setInteractions(prev => ({
                ...prev,
                [shopId]: { ...prev[shopId], wishlist: wasInWishlist }
            }));
        }
        return false;
    }, [isLoggedIn, interactions, queryClient]);

    // Helper to get state safely
    const getInteraction = useCallback((shopId: string) => {
        return interactions[shopId] || { favorite: false, wishlist: false, hasReview: false };
    }, [interactions]);

    return (
        <ShopInteractionsContext.Provider value={{
            interactions,
            fetchInteractionsForShops,
            setBulkInteractions,
            toggleFavorite,
            toggleWishlist,
            getInteraction,
            isLoading
        }}>
            {children}
        </ShopInteractionsContext.Provider>
    );
}

export function useShopInteractions() {
    const context = useContext(ShopInteractionsContext);
    if (context === undefined) {
        throw new Error('useShopInteractions must be used within a ShopInteractionsProvider');
    }
    return context;
}
